import React from "react";
import OrderDTO from "@/dto/order/OrderDTO";
import ShopType from "@/enum/ShopType";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { Button, message, Popover } from "antd";
import ProxyImage from "@/components/ProxyImage";
import AutoLoadingButton from "@/components/AutoLoadingButton";
import { tbPrivacyProtectionSecretExtend } from "@/services/api/tb/orderSecurity";
import { useUpdate } from "ahooks";
import ConsigneePhoneTbDecryptButton from "./ConsigneeColumn/ConsigneePhoneTbDecryptButton";
import { observer } from "mobx-react-lite";
import HighlightWithOnce from "@/components/HighlightWithOnce";
import { useModalStore } from "@/hooks/Modal/hooks";
import ReportAllowedCallModal from "./ReportAllowedCallModal";

interface AddressProps {
    id?: string;

    order: OrderDTO;

    useLocalAddress?: boolean;

    shopType?: ShopType;

    replayMask?: boolean;

    onOrderUpdate?(orderId: number, orderData: any): void;

    onConsigneeChange?(): void;
}

const DY_SUCCESSFUL_ORDER_DECRYPTED_PHONE_TEXT = "该订单已完结，抖店平台不允许查看手机号";

export function joinTownAndStreet(town: any, street: any) {
    if (!town) {
        return street;
    }

    if (!street) {
        return town;
    }

    // 部分平台在street重复添加town
    if (street.indexOf(town) === 0) {
        return street;
    }

    return town + street;
}

const Address: React.FC<AddressProps> = ({
    id,
    order,
    useLocalAddress,
    shopType,
    replayMask,
    onOrderUpdate,
}: AddressProps) => {
    const update = useUpdate();
    const modalStore = useModalStore();
    const { consignee } = order;

    function buildPhone(
        mobile: string,
        telephone: string,
        privacyProtectionExpireTime?: string,
        showTBMobileTip?: boolean,
    ) {
        const phone = mobile && telephone ? `${mobile} ${telephone}` : mobile || telephone;

        if (!phone) {
            return null;
        }

        const isPhoneErrorStyle = phone?.indexOf(DY_SUCCESSFUL_ORDER_DECRYPTED_PHONE_TEXT) > -1;
        const is700VirtualMobile = mobile?.length === 15 && mobile?.startsWith("700");

        let href = "#";

        const buildPrivacyProtectionTip = () => {
            let tip: React.ReactNode = "";

            if (is700VirtualMobile) {
                // tip = "此号码为虚拟号，如需联系买家请先报备外呼主号(教程)后可直接拨打该号码进行联系";
                tip = "此号码为虚拟号，如需联系买家请先报备外呼主号后可直接拨打该号码进行联系";
            } else if (shopType === ShopType.TAOBAO || shopType === ShopType.TAOBAO_DISTRIBUTION) {
                tip = <div>此号码为虚拟号，虚拟号可直接发货/联系消费者，点击可查看虚拟号详细介绍</div>;
                if (privacyProtectionExpireTime) {
                    tip = (
                        <div>
                            {tip}
                            <div className="tc">
                                <span>
                                    虚拟号有效期：{privacyProtectionExpireTime}
                                    <AutoLoadingButton
                                        type="link"
                                        size="small"
                                        onClick={async () => {
                                            const result = await tbPrivacyProtectionSecretExtend({
                                                shopId: order.shopId,
                                                orderId: order.id,
                                                originalId: order.originalId,
                                                mergeSplitStatus: order.mergeSplitStatus,
                                            });

                                            order.consignee.privacyProtectionExpireTime = result.expireTime;

                                            update();

                                            message.success("延长隐私号时长成功");
                                        }}
                                    >
                                        延长隐私号时长
                                    </AutoLoadingButton>
                                </span>
                            </div>
                        </div>
                    );
                }
                href =
                    "https://huodong.taobao.com/wow/z/mt/default/HmZpDk4CTanZyJKDeEps?spm=a1zfx5.my_create_page.0.0.fde22251cebrhn";
            } else if (shopType === ShopType.ALIBABA) {
                tip = <div>此号码为虚拟号，虚拟号可直接发货/联系消费者，点击可查看虚拟号详细介绍</div>;
            } else if (shopType === ShopType.PINDUODUO) {
                tip = (
                    <ProxyImage
                        src="//img.alicdn.com/imgextra/i2/54458674/O1CN018fX0NO2DwkCwNb5wc_!!54458674.png"
                        width="400"
                    />
                );
                href = "https://mms.pinduoduo.com/daxue/detail?courseId=3974";
            } else if (
                shopType === ShopType.DOUDIAN ||
                shopType === ShopType.DOUDIAN_IOP ||
                shopType === ShopType.DOUDIAN_SUPPLIER
            ) {
                tip = (
                    <>
                        此号码为虚拟号，如需联系买家，可直接拨打号码，听到语音提示后，输入姓名后4位分机号码
                        <br />
                        此号码为虚拟号，不得复制用于发货，如需消费者真实手机号，需至商家后台报备获取
                    </>
                );
                href = "https://bytedance.feishu.cn/docx/doxcngwjMK3FMmEkxKoCmEVn9gh";
            } else if ([ShopType.JD, ShopType.JD_PURCHASE].includes(shopType)) {
                tip =
                    "此号码为虚拟号，如需联系买家可直接拨打号码，听到语音提示后输入4位分机号码；快递柜目前只支持投递丰巢快递柜";
            } else if (shopType === ShopType.ALI_C2M) {
                tip = (
                    <>
                        针对虚拟号订单，淘工厂平台将会用虚拟号替换订单中的收货人真实手机号。
                        <br />
                        虚拟号形式：11位主机号+4位分机号（当前使用4位分机号，平台会根据实际情况对分机号长度进行调整，如可能使用3位或5位分机号）。虚拟号示例：15912341234转4567
                    </>
                );
            } else if (
                [ShopType.CHANNEL_SHOP, ShopType.CHANNEL_SHOP_DROPSHIP, ShopType.CHANNEL_SHOP_SUPPLIER].includes(
                    shopType,
                )
            ) {
                tip = (
                    <div>
                        <div>此号码为虚拟号，商家和物流公司可在虚拟号有效期内使用虚拟号联系买家，</div>
                        <div className="tc">
                            虚拟号有效期：
                            {privacyProtectionExpireTime || ""}，
                            <a
                                href="https://support.weixin.qq.com/cgi-bin/mmsupportacctnodeweb-bin/pages/EQBKa4AIver3UPux"
                                target="_blank"
                            >
                                点击可查看虚拟号详细介绍
                            </a>
                        </div>
                    </div>
                );
            } else if (shopType === ShopType.XIAOHONGSHU) {
                tip = (
                    <div>
                        隐私号将由11位主机号码+4位分机号码组成，可先拨打隐私号11位主机号，并根据提示音输入4位分机号，成功输入分机号后可以联系到收货人，如遇忙音可重新拨打。
                    </div>
                );
            } else {
                tip = (
                    <div>
                        此号码为虚拟号，虚拟号可直接发货/联系消费者，不可发短信，如需联系请拨通虚拟号之后输入姓名或地址后面的4位分机号
                    </div>
                );
            }

            return tip;
        };

        const virtualNo =
            !useLocalAddress && consignee?.privacyProtection ? (
                <Popover content={buildPrivacyProtectionTip()}>
                    <a
                        href={href}
                        className="ml5"
                        target={href === "#" ? undefined : "_blank"}
                        style={{ color: "#a94442" }}
                    >
                        虚拟号
                        <QuestionCircleOutlined />
                    </a>
                </Popover>
            ) : null;

        const showReportAllowedCall =
            is700VirtualMobile &&
            [ShopType.JD, ShopType.JD_PURCHASE, ShopType.TAOBAO, ShopType.TAOBAO_DISTRIBUTION].includes(shopType);

        return (
            <span className={isPhoneErrorStyle ? "error" : undefined}>
                {phone}
                {phone.includes("****") && showTBMobileTip ? (
                    <ConsigneePhoneTbDecryptButton order={order} onOrderUpdate={onOrderUpdate} />
                ) : null}
                {virtualNo}
                {showReportAllowedCall ? (
                    <Button
                        type="link"
                        className="ml5"
                        size="small"
                        onClick={() => {
                            modalStore.openModal(ReportAllowedCallModal, {
                                shopId: order.shopId,
                                orderId: order.id,
                                virtualNo: consignee.mobile,
                                shopType,
                            });
                        }}
                    >
                        报备主叫号码
                    </Button>
                ) : null}
                ，
            </span>
        );
    }

    let maskObject = replayMask
        ? {
              "data-openreplay-masked": true,
          }
        : {};

    if (useLocalAddress) {
        const phone = buildPhone(consignee.localMobile, consignee.localTelephone);

        return (
            <span id={id} {...maskObject}>
                {consignee.localName ? `${consignee.localName}，` : null}
                {phone}
                {consignee.localProvince}
                {consignee.localCity}
                {consignee.localDistrict}
                <HighlightWithOnce
                    text={consignee.localStreet}
                    highlightTexts={consignee._localStreetDoubleAddressKeywords}
                />
            </span>
        );
    }
    const phone = buildPhone(
        consignee.mobile,
        consignee.telephone,
        consignee.privacyProtectionExpireTime,
        consignee.showTBMobileTip,
    );
    const street = joinTownAndStreet(consignee.town, consignee.street);

    return (
        <span id={id} {...maskObject}>
            {consignee.name ? `${consignee.name}，` : null}
            {phone}
            {consignee.province}
            {consignee.city}
            {consignee.district}
            <HighlightWithOnce text={street} highlightTexts={consignee._streetDoubleAddressKeywords} />
        </span>
    );
};

export default observer(Address);
